'use client';
import { alpha, Box, Button, Theme, Typography } from '@mui/material';

interface OutcomeBarRowProps {
  label: string;
  /** Raw probability in the 0-1 range (used for both the bar fill and the multiplier). */
  price: number;
  /** Already-formatted probability, without the trailing "%" (see formatProbability). */
  percent: string;
  isYes: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

// Multiplier = potential payout per $1 staked if this outcome wins (1 / price),
// which is standard prediction-market odds math — not a field the API returns.
const formatMultiplier = (price: number): string => {
  if (!price || price <= 0) return '--';
  const multiplier = 1 / price;
  return `${multiplier.toFixed(multiplier >= 10 ? 1 : 2)}x`;
};

// Matches the mobile pill sizing used across the other card variants
// (OutcomeTradeRow / MultiOutcomeCard / CustomBinaryCard) for consistency.
const getPillStyles = (theme: Theme, isYes: boolean) => {
  const variant = isYes ? theme.palette.success : theme.palette.error;
  return {
    minWidth: 54,
    height: 36,
    borderRadius: '999px',
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '13.5px',
    lineHeight: 1,
    boxShadow: 'none',
    bgcolor: variant.bg,
    color: theme.palette.mode === 'dark' ? variant.light : variant.dark,
    border: '1px solid',
    borderColor: variant.border,
    padding: '0 15px',
    '&:hover': {
      bgcolor: alpha(variant.main, 0.25),
      boxShadow: 'none',
    },
  };
};

export function OutcomeBarRow({ label, price, percent, isYes, onClick }: OutcomeBarRowProps) {
  const fillPercent = Math.min(100, Math.max(0, price * 100));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, py: '8px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: '6px' }}>
        <Typography sx={{ fontWeight: 500, fontSize: '14.5px', color: 'text.secondary' }}>
          {label}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '5px',
            borderRadius: '10px',
            bgcolor: 'action.selected',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${fillPercent}%`,
              borderRadius: '10px',
              bgcolor: (theme: Theme) =>
                isYes ? theme.palette.success.main : theme.palette.error.main,
            }}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Typography
          sx={{
            fontFamily: 'var(--font-plex-mono), IBM Plex Mono, monospace',
            fontSize: '12px',
            color: 'text.disabled',
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {formatMultiplier(price)}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={onClick}
          sx={(theme) => ({ ...getPillStyles(theme, isYes), flexShrink: 0 })}
        >
          {percent}%
        </Button>
      </Box>
    </Box>
  );
}
